import type { AuthTokens } from '@lex/contracts';

export interface TokenStorage {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(tokens: AuthTokens): void;
  clear(): void;
}

export function createMemoryTokenStorage(): TokenStorage {
  let access: string | null = null;
  let refresh: string | null = null;
  return {
    getAccessToken: () => access,
    getRefreshToken: () => refresh,
    setTokens: ({ accessToken, refreshToken }) => {
      access = accessToken;
      refresh = refreshToken;
    },
    clear: () => {
      access = null;
      refresh = null;
    },
  };
}

export function createLocalStorageTokenStorage(
  key = 'lex_auth',
  storage: Storage = localStorage
): TokenStorage {
  let cache: AuthTokens | null = null;
  const read = (): AuthTokens | null => {
    if (cache) return cache;
    const raw = storage.getItem(key);
    if (!raw) return null;
    try {
      cache = JSON.parse(raw) as AuthTokens;
      return cache;
    } catch {
      return null;
    }
  };
  return {
    getAccessToken: () => read()?.accessToken ?? null,
    getRefreshToken: () => read()?.refreshToken ?? null,
    setTokens: (tokens) => {
      cache = tokens;
      storage.setItem(key, JSON.stringify(tokens));
    },
    clear: () => {
      cache = null;
      storage.removeItem(key);
    },
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  storage?: TokenStorage;
  fetchImpl?: typeof fetch;
  /** Endpoint обновления токенов; по умолчанию — клиентский /auth/refresh. */
  refreshPath?: string;
  /** Вызывается, когда refresh не удался (напр. редирект на логин). */
  onUnauthorized?: () => void;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
  signal?: AbortSignal;
}

export class ApiClient {
  readonly baseUrl: string;
  readonly storage: TokenStorage;
  private readonly fetchImpl: typeof fetch;
  private readonly refreshPath: string;
  private readonly onUnauthorized?: () => void;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.storage = options.storage ?? createMemoryTokenStorage();
    // bind(globalThis): иначе вызов this.fetchImpl(...) ломает нативный fetch
    // в браузере («Illegal invocation»).
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
    this.refreshPath = options.refreshPath ?? '/api/v1/auth/refresh';
    if (options.onUnauthorized) this.onUnauthorized = options.onUnauthorized;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const auth = options.auth ?? true;
    const res = await this.rawRequest(path, options, auth);

    if (res.status === 401 && auth && this.storage.getRefreshToken()) {
      const refreshed = await this.refresh();
      if (refreshed) {
        const retry = await this.rawRequest(path, options, auth);
        return this.parse<T>(retry);
      }
      this.onUnauthorized?.();
    }

    return this.parse<T>(res);
  }

  private async rawRequest(
    path: string,
    options: RequestOptions,
    auth: boolean
  ): Promise<Response> {
    const headers: Record<string, string> = { ...options.headers };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (auth) {
      const token = this.storage.getAccessToken();
      if (token) headers['authorization'] = `Bearer ${token}`;
    }

    return this.fetchImpl(`${this.baseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  }

  private async parse<T>(res: Response): Promise<T> {
    const contentType = res.headers.get('content-type') ?? '';
    const payload: unknown = contentType.includes('application/json')
      ? await res.json().catch(() => null)
      : await res.text();

    if (!res.ok) {
      const message =
        typeof payload === 'object' && payload && 'message' in payload
          ? String((payload as { message: unknown }).message)
          : `HTTP ${res.status}`;
      throw new ApiError(res.status, message, payload);
    }
    return payload as T;
  }

  /** Пробует обновить access-токен. Возвращает true при успехе. */
  async refresh(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;

    const refreshToken = this.storage.getRefreshToken();
    if (!refreshToken) return false;

    this.refreshPromise = (async () => {
      try {
        const res = await this.fetchImpl(`${this.baseUrl}${this.refreshPath}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) {
          this.storage.clear();
          return false;
        }
        const tokens = (await res.json()) as AuthTokens;
        this.storage.setTokens(tokens);
        return true;
      } catch {
        return false;
      } finally {
        // Сбрасываем промис в следующем тике, чтобы параллельные вызовы переиспользовали его.
        queueMicrotask(() => {
          this.refreshPromise = null;
        });
      }
    })();

    return this.refreshPromise;
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }
}
